import { Box, Grid, styled, Typography, useTheme } from '@mui/material';
import React, { useMemo } from 'react';
import { endDateFromTimePointType } from '../../../api/timeranges/utils';
import {
  TimePointSelectorContext,
  useDomainContext,
  useFilterSectionsContext,
  useGlobalFilterContext,
  useGlobalLocaleContext,
  useGlobalSegmentationLevel1Context,
  useLatestPreferencesContext,
  useSegmentationSectionsContext,
  useTimePointSelectorContext,
} from '../../../context/contexts';
import { format, getMomentDate, monthEnd, now } from '../../../date/date-utils';
import { StoryboardDateUtils } from '../../../settings/dashboard-configs/utils';
import {
  filterBarGrey,
  filterBarRed,
  segmentationMainColor,
} from '../../theme';
import { FilterBar } from '../filter/filterbar/FilterBar';
import { Markdown } from '../misc/Markdown';
import { StoryboardItem } from '../storyboard-components/common/StoryboardItem';
import { CalendarYearMonthlyTimesliderGranularity } from '../timeslider/constants';
import {
  buildTimePointSelectorStateWithGranularity,
  useTimePointSelector,
  useTimePointSelectorWithPersistence,
} from '../timeslider/time-point-selector-hooks';
import { TimeSlider } from '../timeslider/TimeSlider';
import {
  Granularity,
  Months,
  TimePointSelectorState,
  TimeSliderGranularity,
} from '../timeslider/types';
import { useDashboardPageOptionsContext } from './contexts';
import {
  DashboardPageOptions,
  DashboardPageTimeOptions,
  PageConfig,
} from './types';

const StyledWrapper = styled('div')`
  height: 100%;
  margin-left: 3rem;
`;

const headerHeight = '60px';
const filterBarHeight = '60px';

const defaultTimeOptions: DashboardPageTimeOptions = {
  timeSliderEnabled: false,
};

const getDefaultGranularity = (
  timeOptions: DashboardPageTimeOptions
): TimeSliderGranularity | undefined => {
  if (timeOptions.timeSliderEnabled) {
    return timeOptions.defaultGranularity as TimeSliderGranularity | undefined;
  }
  return undefined;
};

const PageWithComponentConfigs = ({
  pageConfig,
  pageOptions,
}: {
  pageConfig: Extract<PageConfig, { configType: 'componentConfigs' }>;
  pageOptions: DashboardPageOptions;
}) => {
  const theme = useTheme();
  const domainPreferences = useLatestPreferencesContext();
  const [timePointSelectorState] = useTimePointSelectorContext();
  const firstMonthOfYear =
    domainPreferences.finYearStartMonth ?? Months.January;
  const dateFromTimeSlider = endDateFromTimePointType(
    timePointSelectorState.date,
    timePointSelectorState.selectedGranularity,
    firstMonthOfYear
  );
  const defaultReferenceDate = format(monthEnd(now()));
  const { timeOptions = defaultTimeOptions } = pageOptions;
  const { timeSliderEnabled } = timeOptions;
  // `availableTimeRanges` is typed as `Record<Granularity, TimeRange>`, but
  // entries at MONTH granularity are always `Date` at runtime (see
  // `getTimeRangesFromTimePoints`). Narrow with `instanceof` to satisfy types.
  const warehouseLatestMonthRaw =
    timePointSelectorState.availableTimeRanges[Granularity.MONTH]?.max;
  const warehouseLatestMonth =
    warehouseLatestMonthRaw instanceof Date
      ? warehouseLatestMonthRaw
      : undefined;
  const baseDateForPage = timeSliderEnabled
    ? dateFromTimeSlider
    : (timeOptions.referenceDate ??
      (timeOptions.useLatestWarehouseMonth && warehouseLatestMonth
        ? format(warehouseLatestMonth.valueOf())
        : defaultReferenceDate));
  const storyboardDateUtils = useMemo(
    () => new StoryboardDateUtils(getMomentDate(baseDateForPage).valueOf()),
    [baseDateForPage]
  );
  const filterTrayEnabled = pageOptions.filterTray?.enabled ?? true;
  const segmentationLevel1TrayEnabled =
    pageOptions.segmentationLevel1?.enabled ?? true;

  return (
    <Grid
      item
      container
      direction={'column'}
      sx={{
        height: `calc(100% - ${headerHeight} - ${
          filterTrayEnabled || segmentationLevel1TrayEnabled
            ? filterBarHeight
            : '0px'
        })`,
      }}
      wrap="nowrap"
    >
      <Box sx={{ flex: '0 0 auto' }}>
        {pageConfig.header && <Markdown markdown={pageConfig.header} />}
      </Box>
      <Box sx={{ flex: '1 1 auto', minHeight: 0 }}>
        <Grid
          container
          spacing={0} // No gap between grid items
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${pageConfig.gridCols ?? 3}, minmax(0,1fr))`,
            gridTemplateRows: `repeat(${pageConfig.gridRows ?? 2}, minmax(0,1fr))`,
            paddingLeft: '0.5rem',
            gridAutoRows: '1fr',
            height: '100%',
            [theme.breakpoints.down('lg')]: {
              gridTemplateColumns: `repeat(2, 1fr)`,
              height: 'unset',
            },
          }}
        >
          {pageConfig
            .components({ domainPreferences, storyboardDateUtils })
            .map((component, index) => (
              <StoryboardItem
                key={`${component.chartId}-${index}`}
                config={component}
                pageOptions={pageOptions}
                layoutProps={{
                  padding: '0.25rem',
                }}
              />
            ))}
        </Grid>
      </Box>
      <Box sx={{ flex: '0 0 auto' }}>
        {pageConfig.footer && <Markdown markdown={pageConfig.footer} />}
      </Box>
    </Grid>
  );
};

const PageWithPageComponent = ({
  pageConfig,
  pageOptions,
}: {
  pageConfig: Extract<PageConfig, { configType: 'pageComponent' }>;
  pageOptions: DashboardPageOptions;
}) => {
  const filterTrayEnabled = pageOptions.filterTray?.enabled ?? true;
  const segmentationLevel1TrayEnabled =
    pageOptions.segmentationLevel1?.enabled ?? true;

  return (
    <Box
      sx={{
        height: `calc(100% - ${headerHeight} - ${
          filterTrayEnabled || segmentationLevel1TrayEnabled
            ? filterBarHeight
            : '0px'
        })`,
      }}
    >
      {pageConfig.header && <Markdown markdown={pageConfig.header} />}
      {<pageConfig.pageComponent />}
      {pageConfig.footer && <Markdown markdown={pageConfig.footer} />}
    </Box>
  );
};

const DashboardPageInner = ({ pageConfig }: { pageConfig: PageConfig }) => {
  const filterHandle = useGlobalFilterContext();
  const segmentationHandle = useGlobalSegmentationLevel1Context();
  const pageOptions = useDashboardPageOptionsContext();
  const {
    timeOptions = defaultTimeOptions,
    filterTray,
    segmentationLevel1,
  } = pageOptions;
  const filterTrayEnabled = filterTray?.enabled ?? true;
  const segmentationLevel1TrayEnabled = segmentationLevel1?.enabled ?? true;
  const locale = useGlobalLocaleContext();
  const filterSections = useFilterSectionsContext();
  const segmentationSections = useSegmentationSectionsContext();
  const [timePointSelectorState] = useTimePointSelectorContext();
  const domainPreferences = useLatestPreferencesContext();
  const firstMonthOfYear =
    domainPreferences.finYearStartMonth ?? Months.January;
  const dateFromTimeSlider = endDateFromTimePointType(
    timePointSelectorState.date,
    timePointSelectorState.selectedGranularity,
    firstMonthOfYear
  );
  const defaultReferenceDate = format(monthEnd(now()));
  const { timeSliderEnabled } = timeOptions;
  // MONTH-granularity entries in `availableTimeRanges` are always `Date` at
  // runtime; narrow with `instanceof` to satisfy the broader union type.
  const warehouseLatestMonthRaw =
    timePointSelectorState.availableTimeRanges[Granularity.MONTH]?.max;
  const warehouseLatestMonth =
    warehouseLatestMonthRaw instanceof Date
      ? warehouseLatestMonthRaw
      : undefined;
  // Resolution order when the timeslider is hidden:
  //   1. explicit `referenceDate` (frozen / hand-picked anchor)
  //   2. `useLatestWarehouseMonth` -> warehouse `availableTimeRanges` max
  //      (mirrors the PBS timeslider's initial date)
  //   3. fallback to current month-end so charts that need "today" keep
  //      working when warehouse data lags by a month
  const baseDateForPage = timeSliderEnabled
    ? dateFromTimeSlider
    : (timeOptions.referenceDate ??
      (timeOptions.useLatestWarehouseMonth && warehouseLatestMonth
        ? format(warehouseLatestMonth.valueOf())
        : defaultReferenceDate));
  const storyboardDateUtils = useMemo(
    () => new StoryboardDateUtils(getMomentDate(baseDateForPage).valueOf()),
    [baseDateForPage]
  );

  return (
    <StyledWrapper>
      <Grid
        item
        xs
        container
        sx={{
          padding: 2,
          height: headerHeight,
          overflow: 'hidden',
        }}
      >
        <Grid item container flexWrap="nowrap">
          <Grid
            item
            container
            xs={timeSliderEnabled ? 4 : 12}
            wrap="nowrap"
            alignItems={'center'}
          >
            <Typography variant="h5" color="textPrimary">
              {pageConfig.title[locale.selected]}
            </Typography>
          </Grid>
          {timeSliderEnabled && (
            <TimeSlider type={timeOptions.timeSliderType} />
          )}
        </Grid>
      </Grid>
      {filterTrayEnabled || segmentationLevel1TrayEnabled ? (
        <Grid
          item
          container
          sx={{ height: filterBarHeight, overflow: 'auto' }}
          justifyContent={'space-between'}
        >
          {filterTrayEnabled ? (
            <Grid item xs={8}>
              <FilterBar
                filterHandle={filterHandle}
                filterSections={filterSections}
              />
            </Grid>
          ) : null}
          {segmentationLevel1TrayEnabled ? (
            <Grid item xs={4}>
              <FilterBar
                filterHandle={segmentationHandle}
                filterSections={segmentationSections}
                options={{
                  justifyContent: 'flex-end',
                  filterPillColors: {
                    default: segmentationMainColor,
                    stale: filterBarGrey,
                    notEqual: filterBarRed,
                  },
                  allowDelete: true,
                }}
              />
            </Grid>
          ) : null}
        </Grid>
      ) : null}
      {pageConfig.configType === 'componentConfigs' ? (
        <PageWithComponentConfigs
          pageConfig={pageConfig}
          pageOptions={pageOptions}
        />
      ) : (
        <PageWithPageComponent
          pageConfig={pageConfig}
          pageOptions={pageOptions}
        />
      )}
    </StyledWrapper>
  );
};

// ---------------------------------------------------------------------------
// PageTimeContextScope
//
// Every dashboard page passes through this component. It inspects `timeOptions`
// and, when needed, provides a scoped `TimePointSelectorContext` so that
// `DashboardPageInner` (and the `TimeSlider`) operate at the correct
// granularity / reference date.  When no override is required it simply
// renders children against the global context.
// ---------------------------------------------------------------------------

const ReferenceDateScope = ({
  referenceDate,
  children,
}: {
  referenceDate: string;
  children: React.ReactNode;
}) => {
  const [globalState] = useTimePointSelectorContext();
  const modifiedState: TimePointSelectorState = {
    ...globalState,
    selectedGranularity: CalendarYearMonthlyTimesliderGranularity,
    date: getMomentDate(referenceDate).toDate(),
  };
  const domain = useDomainContext();
  const handle = useTimePointSelectorWithPersistence(domain, modifiedState);
  return (
    <TimePointSelectorContext.Provider value={handle}>
      {children}
    </TimePointSelectorContext.Provider>
  );
};

const GranularityScope = ({
  granularity,
  children,
}: {
  granularity: TimeSliderGranularity;
  children: React.ReactNode;
}) => {
  const [globalState] = useTimePointSelectorContext();
  const rangeMax = globalState.availableTimeRanges[granularity.value]?.max;
  const mountKey =
    rangeMax != null ? `${granularity.id}-${String(rangeMax)}` : granularity.id;
  const init = buildTimePointSelectorStateWithGranularity(
    globalState,
    granularity
  );
  return (
    <GranularityScopeInner key={mountKey} init={init}>
      {children}
    </GranularityScopeInner>
  );
};

const GranularityScopeInner = ({
  init,
  children,
}: {
  init: TimePointSelectorState;
  children: React.ReactNode;
}) => {
  const handle = useTimePointSelector(init);
  return (
    <TimePointSelectorContext.Provider value={handle}>
      {children}
    </TimePointSelectorContext.Provider>
  );
};

const PageTimeContextScope = ({
  timeOptions,
  children,
}: {
  timeOptions: DashboardPageTimeOptions;
  children: React.ReactNode;
}): React.ReactElement => {
  if (timeOptions.timeSliderEnabled === false) {
    if (
      timeOptions.referenceDate &&
      getMomentDate(timeOptions.referenceDate).isValid()
    ) {
      return (
        <ReferenceDateScope referenceDate={timeOptions.referenceDate}>
          {children}
        </ReferenceDateScope>
      );
    }
    return <>{children}</>;
  }

  const granularity = getDefaultGranularity(timeOptions);
  if (granularity) {
    return (
      <GranularityScope granularity={granularity}>{children}</GranularityScope>
    );
  }

  return <>{children}</>;
};

const DashboardPage = ({ pageConfig }: { pageConfig: PageConfig }) => {
  const { timeOptions = defaultTimeOptions } = pageConfig.pageOptions;
  return (
    <PageTimeContextScope timeOptions={timeOptions}>
      <DashboardPageInner pageConfig={pageConfig} />
    </PageTimeContextScope>
  );
};
export default DashboardPage;
